import { useMemo, useState } from "react";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { Switch } from "@/shared/components/shadcn/Switch";
import { DateCalendar } from "@/shared/components/ui/DateCalendar";
import { DateTimePickerLayout } from "@/shared/components/ui/DateTimePickerLayout";
import { FormField } from "@/shared/components/ui/FormField";
import type { DialogDismissReason } from "@/shared/components/ui/FullScreenDialog";
import { InfoRow } from "@/shared/components/ui/InfoRow";
import { Tabs } from "@/shared/components/ui/Tabs";
import { calendarDate, calendarDateKey } from "@/shared/utils/calendarDate";
import { getBusinessDateKey } from "@/shared/utils/formatters";
import type { CalendarEvent } from "../types/calendarEventTypes";
import { eventRange } from "../utils/eventTime";

export interface CalendarEventTimeDraft {
  start: string;
  end: string;
  isAllDay: boolean;
}

interface CalendarEventDateTimePickerProps {
  open: boolean;
  value: CalendarEventTimeDraft;
  endConfirmed: boolean;
  timezone: string;
  original?: Pick<CalendarEvent, "startAt" | "endAt" | "isAllDay">;
  onApply: (value: CalendarEventTimeDraft) => void;
  onClose: (reason?: DialogDismissReason) => void;
}

export function CalendarEventDateTimePicker({ open, ...props }: CalendarEventDateTimePickerProps) {
  return open ? <CalendarEventDateTimeEditor {...props} /> : null;
}

function CalendarEventDateTimeEditor({
  value, endConfirmed, timezone, original, onApply, onClose,
}: Omit<CalendarEventDateTimePickerProps, "open">) {
  const [draft, setDraft] = useState(value);
  const [endSelected, setEndSelected] = useState(endConfirmed);
  const [endpoint, setEndpoint] = useState<"start" | "end">("start");
  const [month, setMonth] = useState(() => calendarDate(value.start.slice(0, 10)));
  const [times, setTimes] = useState({
    start: value.isAllDay ? "09:00" : value.start.slice(11),
    end: value.isAllDay ? "10:00" : value.end.slice(11),
  });
  const selectedDay = calendarDate(draft[endpoint].slice(0, 10));
  const startDay = calendarDate(draft.start.slice(0, 10));
  const endDay = calendarDate(draft.end.slice(0, 10));
  const today = getBusinessDateKey(new Date(), timezone);
  const error = useMemo(() => {
    try {
      eventRange(draft.start, draft.end, draft.isAllDay, timezone, original, today);
      return undefined;
    } catch (cause) {
      return cause instanceof Error ? cause.message : "Choose a valid date and time.";
    }
  }, [draft, timezone, original, today]);

  function change(next: Partial<CalendarEventTimeDraft>) {
    setDraft((current) => ({ ...current, ...next }));
  }

  function changeEndpoint(key: "start" | "end", value: string) {
    if (key === "end") setEndSelected(true);
    change({ [key]: value });
  }

  function selectEndpoint(next: "start" | "end") {
    setEndpoint(next);
    setMonth(calendarDate(draft[next].slice(0, 10)));
  }

  function toggleAllDay(checked: boolean) {
    if (checked) setTimes({ start: draft.start.slice(11), end: draft.end.slice(11) });
    change({
      isAllDay: checked,
      start: `${draft.start.slice(0, 10)}${checked ? "" : `T${times.start}`}`,
      end: `${draft.end.slice(0, 10)}${checked ? "" : `T${times.end}`}`,
    });
  }

  function apply() {
    if (error) return;
    onApply({ start: draft.start, end: draft.end, isAllDay: draft.isAllDay });
    onClose();
  }

  return (
    <DateTimePickerLayout
      onClose={onClose}
      footer={<>
        {error && <p id="event-picker-error" role="alert" className="text-sm text-destructive">{error}</p>}
        <Button className="w-full" disabled={!!error} onClick={apply}>Apply</Button>
      </>}
      calendar={<>
        <Tabs
          label="Date range"
          value={endpoint}
          onValueChange={selectEndpoint}
          options={(["start", "end"] as const).map((key) => {
            const label = draft.isAllDay ? key === "start" ? "Start date" : "Last day" : key === "start" ? "Starts" : "Ends";
            return {
              value: key,
              ariaLabel: label,
              label: <span className="flex flex-col gap-0.5">
                <span>{label}</span>
                <span className="text-xs font-normal">{calendarDate(draft[key].slice(0, 10)).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</span>
              </span>,
            };
          })}
        />
        <DateCalendar
          today={calendarDate(today)}
          selectedDay={selectedDay}
          selectedRange={{ from: startDay, to: endDay }}
          disabled={[
            { before: calendarDate(today) },
            ...(endpoint === "end" ? [{ before: startDay }] : endSelected ? [{ after: endDay }] : []),
          ]}
          month={month}
          onMonthChange={setMonth}
          onDateSelect={(date) => {
            changeEndpoint(endpoint, `${calendarDateKey(date)}${draft.isAllDay ? "" : `T${draft[endpoint].slice(11)}`}`);
            setMonth(date);
          }}
        />
      </>}
    >
      <div className="date-time-picker-scroll min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-1">
        <InfoRow
          title="All day"
          density="compact"
          actions={<Switch aria-label="All day" checked={draft.isAllDay} onCheckedChange={toggleAllDay} />}
        />
        {!draft.isAllDay && <div className="grid grid-cols-2 gap-3">
          {(["start", "end"] as const).map((key) => (
            <FormField key={key} id={`event-picker-${key}-time`} label={key === "start" ? "Start time" : "End time"} required>
              <Input
                type="time"
                step={60}
                value={draft[key].slice(11)}
                readOnly={key !== endpoint}
                className={key !== endpoint ? "bg-muted text-muted-foreground" : undefined}
                aria-invalid={key === endpoint && !!error}
                aria-describedby={error ? "event-picker-error" : undefined}
                onChange={(event) => changeEndpoint(key, `${draft[key].slice(0, 10)}T${event.target.value}`)}
              />
            </FormField>
          ))}
        </div>}
        <p className="text-xs text-muted-foreground">
          Choose today or a future date, including outside working hours.
          {draft.isAllDay ? " Includes the entire last day." : " Repeated clock-change times use the earlier occurrence for newly entered times; unchanged saved or prefilled times keep their original occurrence."}
        </p>
      </div>
    </DateTimePickerLayout>
  );
}
